import {
  CONTENT_STATUS_LABELS,
  formatDate,
  type ActivityDto,
  type AnnualWheelThemeDto,
  type ContentItemDto,
} from '@nova/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useChartTheme } from '../../app/ThemeProvider.js';
import { EmptyState } from '../../components/EmptyState.js';
import { ChevronRight } from 'lucide-react';
import { api, query } from '../../lib/api.js';
import { useAreaColor } from '../../lib/area-color.js';
import { cn } from '../../lib/cn.js';
import { buildPhases, nextActivity, type PhaseSegment } from './timeline-phases.js';
import {
  buildTimeline,
  daysBetween,
  monthSegments,
  startOfDay,
  type Grouping,
  type TimelineBar,
} from './timeline.js';

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];

const LABEL_WIDTH = 280;
/** On a phone the title column would eat the whole screen. */
const LABEL_WIDTH_NARROW = 160;
const ROW_HEIGHT = 38;
const PHASE_ROW_HEIGHT = 26;

/** Tracks a media query so the label column can shrink on small screens. */
function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches,
  );

  useEffect(() => {
    const query = window.matchMedia('(max-width: 640px)');
    const update = () => setNarrow(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  return narrow;
}

/**
 * The editorial plan as a timeline: what should have happened, what is coming,
 * and who it sits with. Deliberately a reading surface — dates are changed in
 * the detail drawer, not by dragging (dokumentasjon/07-utviklingsplan.md 2.1.1).
 */
export function TimelineView({
  items,
  grouping,
  lookbackDays,
  dayWidth,
  onOpen,
}: {
  items: ContentItemDto[];
  grouping: Grouping;
  lookbackDays: number;
  dayWidth: number;
  onOpen: (item: ContentItemDto) => void;
}) {
  const areaColor = useAreaColor();
  const chart = useChartTheme();
  const scrollRef = useRef<HTMLDivElement>(null);
  const labelWidth = useIsNarrow() ? LABEL_WIDTH_NARROW : LABEL_WIDTH;
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const { data: wheel = [] } = useQuery({
    queryKey: ['annual-wheel'],
    queryFn: () => api.get<AnnualWheelThemeDto[]>('/dashboard/annual-wheel'),
    staleTime: 60 * 60_000,
  });

  const model = useMemo(
    () => buildTimeline(items, { grouping, lookbackDays }),
    [items, grouping, lookbackDays],
  );

  const today = startOfDay(new Date());
  const todayOffset = daysBetween(model.start, today);
  const width = model.totalDays * dayWidth;
  const months = useMemo(() => monthSegments(model.start, model.end), [model.start, model.end]);

  // Open on today rather than at the far left of the history.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    container.scrollLeft = Math.max(0, todayOffset * dayWidth - container.clientWidth / 3);
  }, [todayOffset, dayWidth, grouping, lookbackDays]);

  if (model.groups.length === 0 && model.unplaced.length === 0) {
    return <EmptyState title="Ingen saker å vise på tidslinjen." />;
  }

  const themeFor = (year: number, month: number) =>
    wheel.find((t) => t.year === year && t.month === month)?.theme;

  return (
    <div className="space-y-4" data-testid="timeline">
      <div className="border-hairline rounded-panel overflow-hidden border">
        <div ref={scrollRef} className="overflow-x-auto">
          <div style={{ width: width + labelWidth }}>
            {/* ── Månedsrad og årshjul ─────────────────────────────────── */}
            <div className="border-hairline bg-panel/60 sticky top-0 z-20 border-b">
              <div className="flex">
                <div
                  className="bg-panel/60 border-hairline sticky left-0 z-10 shrink-0 border-r px-3 py-2"
                  style={{ width: labelWidth }}
                >
                  <span className="label-micro">Tidslinje</span>
                </div>
                <div className="relative" style={{ width }}>
                  {months.map((segment) => {
                    const theme = themeFor(segment.year, segment.month);
                    return (
                      <div
                        key={`${segment.year}-${segment.month}`}
                        className="border-hairline absolute top-0 h-full border-l px-2 py-2"
                        style={{
                          left: segment.offsetDays * dayWidth,
                          width: segment.days * dayWidth,
                        }}
                      >
                        <div className="label-micro truncate">
                          {MONTHS[segment.month - 1]} {String(segment.year).slice(2)}
                        </div>
                        {theme && (
                          <div className="text-violet truncate text-[10px]" title={theme}>
                            {theme}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {todayOffset >= 0 && todayOffset <= model.totalDays && (
                    <span
                      className="bg-accent absolute bottom-0 z-10 -translate-x-1/2 whitespace-nowrap rounded-t px-1.5 text-[10px] text-base font-semibold"
                      style={{ left: todayOffset * dayWidth }}
                    >
                      i dag
                    </span>
                  )}
                  <div className="h-[38px]" />
                </div>
              </div>
            </div>

            {/* ── Gruppene ──────────────────────────────────────────────── */}
            <div className="relative">
              {/* I dag-linjen går gjennom hele høyden. */}
              {todayOffset >= 0 && todayOffset <= model.totalDays && (
                <div
                  aria-hidden
                  className="bg-accent pointer-events-none absolute bottom-0 top-0 z-10 w-px"
                  style={{ left: labelWidth + todayOffset * dayWidth }}
                />
              )}

              {model.groups.map((group) => (
                <section key={group.key}>
                  {group.label !== '' && (
                    <div className="border-hairline bg-raised/40 flex border-b">
                      <div
                        className="bg-raised/40 border-hairline sticky left-0 z-10 shrink-0 border-r px-3 py-1.5"
                        style={{ width: labelWidth }}
                      >
                        <div className="flex items-center gap-2">
                          {group.color && (
                            <span
                              aria-hidden
                              className="h-2 w-2 shrink-0 rounded-full"
                              style={{ backgroundColor: areaColor(group.color) }}
                            />
                          )}
                          <span className="text-ink truncate text-xs font-medium">
                            {group.label}
                          </span>
                          <span className="text-ink-soft num-tabular ml-auto text-[11px]">
                            {group.bars.length}
                          </span>
                        </div>
                        {group.context && (
                          <span className="text-ink-soft block truncate text-[10px]">
                            {group.context}
                          </span>
                        )}
                      </div>
                      <div style={{ width }} />
                    </div>
                  )}

                  {group.bars.map((bar) => (
                    <Row
                      key={bar.item.id}
                      bar={bar}
                      start={model.start}
                      dayWidth={dayWidth}
                      width={width}
                      color={areaColor(bar.item.area?.color)}
                      gridColor={chart.grid}
                      labelWidth={labelWidth}
                      expanded={expanded.has(bar.item.id)}
                      onToggle={() => toggle(bar.item.id)}
                      onOpen={onOpen}
                    />
                  ))}
                </section>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Legend />

      {model.unplaced.length > 0 && (
        <section className="border-hairline bg-panel/40 rounded-panel border p-4">
          <p className="label-micro mb-1" data-testid="timeline-unplaced">
            Uplassert ({model.unplaced.length})
          </p>
          <p className="text-ink-soft mb-3 text-xs">
            Saker uten frist og uten publiseringsdato. De kan ikke tegnes på en tidslinje — og er i
            praksis det redaksjonsrådet må ta stilling til.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {model.unplaced.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onOpen(item)}
                className="border-hairline bg-base/50 text-ink hover:border-accent/40 max-w-full truncate rounded-lg border px-2.5 py-1.5 text-left text-[11px] transition"
                style={{ borderLeftColor: areaColor(item.area?.color), borderLeftWidth: 2 }}
                title={`${item.title} — ${CONTENT_STATUS_LABELS[item.status]}`}
              >
                {item.title}
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/** What the bars mean. Short enough to read once and remember. */
function Legend() {
  return (
    <div className="text-ink-soft flex flex-wrap items-center gap-x-5 gap-y-2 px-1 text-[11px]">
      <span className="flex items-center gap-1.5">
        <span className="border-ink-soft h-2.5 w-6 rounded border" />
        Under arbeid
      </span>
      <span className="flex items-center gap-1.5">
        <span className="bg-ink-soft h-2.5 w-6 rounded" />
        Publisert
      </span>
      <span className="flex items-center gap-1.5">
        <span className="bg-ink-soft h-2.5 w-px" />
        Skulle vært ferdig
      </span>
      <span className="flex items-center gap-1.5">
        <span className="bg-ink-soft h-2.5 w-2.5 rounded-full" />
        Faktisk publisert
      </span>
      <span className="flex items-center gap-1.5">
        <span className="border-ink-soft h-2.5 w-6 rounded border border-dashed" />
        Antatt fase
      </span>
      <span className="flex items-center gap-1.5">
        <span className="bg-accent h-2.5 w-px" />I dag
      </span>
      <span className="text-ink-soft/70">Fargen viser produktområde</span>
    </div>
  );
}

function Row({
  bar,
  start,
  dayWidth,
  width,
  color,
  gridColor,
  labelWidth,
  expanded,
  onToggle,
  onOpen,
}: {
  bar: TimelineBar;
  start: Date;
  dayWidth: number;
  width: number;
  color: string;
  gridColor: string;
  labelWidth: number;
  expanded: boolean;
  onToggle: () => void;
  onOpen: (item: ContentItemDto) => void;
}) {
  const { item } = bar;
  const next = nextActivity(item);
  const offset = daysBetween(start, bar.start);
  const span = Math.max(1, daysBetween(bar.start, bar.end) + 1);

  // Clip to the visible window rather than letting history run off to the left.
  const left = Math.max(0, offset) * dayWidth;
  const barWidth = Math.max(dayWidth * 1.5, (span + Math.min(0, offset)) * dayWidth);

  const stopped = ['REJECTED', 'CANCELLED'].includes(item.status);
  const done = item.status === 'PUBLISHED';

  return (
    <>
      <div
        className="border-hairline hover:bg-raised/30 flex border-b transition"
        style={{ height: ROW_HEIGHT }}
      >
        <div
          className="bg-panel/60 border-hairline sticky left-0 z-10 flex shrink-0 items-center gap-1 border-r pl-1 pr-2"
          style={{ width: labelWidth }}
        >
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-label={expanded ? `Skjul faser for ${item.title}` : `Vis faser for ${item.title}`}
            className="text-ink-soft hover:text-ink hover:bg-raised shrink-0 rounded p-1 transition"
          >
            <ChevronRight
              size={13}
              className={cn('transition-transform', expanded && 'rotate-90')}
              aria-hidden
            />
          </button>
          <button
            type="button"
            onClick={() => onOpen(item)}
            title={item.title}
            className="min-w-0 flex-1 text-left"
          >
            <span className="text-ink hover:text-accent block truncate text-[11px] transition">
              {item.title}
            </span>
            {/* A passed date reads a shade brighter — a fact, not an alarm.
                The position against the today line already says the rest. */}
            <span
              className={cn(
                'block truncate text-[10px]',
                next.passed ? 'text-ink' : 'text-ink-soft',
              )}
            >
              {next.label}
              {next.at && ` · ${formatDate(next.at)}`}
            </span>
          </button>
        </div>

        <div className="relative" style={{ width }}>
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              backgroundImage: `repeating-linear-gradient(to right, ${gridColor} 0 1px, transparent 1px ${dayWidth * 7}px)`,
            }}
          />

          <button
            type="button"
            onClick={() => onOpen(item)}
            aria-label={`${item.title} — ${CONTENT_STATUS_LABELS[item.status]}`}
            title={tooltip(bar)}
            className={cn(
              'absolute top-1/2 -translate-y-1/2 rounded transition hover:brightness-110',
              stopped && 'opacity-35',
            )}
            style={{
              left,
              width: barWidth,
              height: 14,
              // Colour always says which product area this belongs to; the fill
              // says how far along it is. Nothing here shouts about lateness.
              backgroundColor: done ? color : `${color}2E`,
              border: `1px solid ${color}`,
            }}
          >
            {/* Når arbeidet skulle vært ferdig. */}
            {bar.deadline && (
              <span
                aria-hidden
                className="absolute bottom-0 top-0 w-px opacity-60"
                style={{
                  left: Math.max(0, daysBetween(bar.start, bar.deadline)) * dayWidth,
                  backgroundColor: color,
                }}
              />
            )}
            {/* Faktisk publisert. */}
            {bar.publishedAt && (
              <span
                aria-hidden
                className="border-panel absolute h-2.5 w-2.5 rounded-full border"
                style={{
                  left: Math.max(0, daysBetween(bar.start, bar.publishedAt)) * dayWidth - 5,
                  backgroundColor: color,
                  top: 1,
                }}
              />
            )}
          </button>

          {/* The moment the list is sorted on. Without it the eye has nothing to
              follow: the bar spans deadline → publication, but what orders the
              list sits at different points inside each bar. */}
          {next.at && !stopped && (
            <span
              aria-hidden
              title={`${next.label} · ${formatDate(next.at)}`}
              className="border-panel absolute top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 border"
              style={{
                left: Math.max(0, daysBetween(start, next.at)) * dayWidth - 4,
                backgroundColor: color,
              }}
            />
          )}
        </div>
      </div>

      {expanded && (
        <PhaseRows
          item={item}
          start={start}
          dayWidth={dayWidth}
          width={width}
          color={color}
          labelWidth={labelWidth}
        />
      )}
    </>
  );
}

/**
 * The phases an item has been through and the ones still ahead. Past phases come
 * from the activity log; future ones are estimated from the deadline and the
 * planned publication, and drawn hatched so the guess is visible as a guess.
 */
function PhaseRows({
  item,
  start,
  dayWidth,
  width,
  color,
  labelWidth,
}: {
  item: ContentItemDto;
  start: Date;
  dayWidth: number;
  width: number;
  color: string;
  labelWidth: number;
}) {
  const { data: activity, isLoading } = useQuery({
    queryKey: ['activity', { entityId: item.id, phases: true }],
    queryFn: () =>
      api
        .get<{ items: ActivityDto[] }>(
          `/activity${query({ entityType: 'content', entityId: item.id, take: 50 })}`,
        )
        .then((response) => response.items),
  });

  const phases: PhaseSegment[] = useMemo(
    () => (activity ? buildPhases(item, activity) : []),
    [item, activity],
  );

  if (isLoading) {
    return (
      <div
        className="border-hairline bg-base/30 flex border-b"
        style={{ height: PHASE_ROW_HEIGHT }}
      >
        <div
          className="bg-base/40 border-hairline sticky left-0 z-10 flex shrink-0 items-center border-r pl-8 pr-2"
          style={{ width: labelWidth }}
        >
          <span className="label-micro truncate">Henter faser …</span>
        </div>
        <div style={{ width }} />
      </div>
    );
  }

  return (
    <>
      {phases.map((segment) => {
        const offset = Math.max(0, daysBetween(start, segment.start));
        const span = Math.max(1, daysBetween(segment.start, segment.end) + 1);

        return (
          <div
            key={segment.phase}
            className="border-hairline bg-base/30 flex border-b"
            style={{ height: PHASE_ROW_HEIGHT }}
          >
            <div
              className="bg-base/40 border-hairline sticky left-0 z-10 flex shrink-0 items-baseline gap-2 border-r pl-8 pr-2"
              style={{ width: labelWidth }}
            >
              <span className="text-ink-soft shrink-0 text-[10px]">{segment.label}</span>
              {/* The dates belong on the row, not only in a tooltip — this is the
                  answer to "when did it start, when should it be done". */}
              <span className="text-ink-soft/70 truncate text-[10px]">
                {formatDate(segment.start)} – {formatDate(segment.end)}
                {!segment.actual && ' (antatt)'}
              </span>
            </div>

            <div className="relative" style={{ width }}>
              <span
                className="absolute top-1/2 -translate-y-1/2 rounded-sm"
                style={{
                  left: offset * dayWidth,
                  width: span * dayWidth,
                  height: 10,
                  backgroundColor: segment.actual ? `${color}44` : 'transparent',
                  border: `1px ${segment.actual ? 'solid' : 'dashed'} ${color}`,
                }}
              />
            </div>
          </div>
        );
      })}
    </>
  );
}

function tooltip(bar: TimelineBar): string {
  const { item } = bar;
  const lines = [
    item.title,
    `Status: ${CONTENT_STATUS_LABELS[item.status]}${item.isOverdue ? ' (forsinket)' : ''}`,
    item.owner ? `Ansvarlig: ${item.owner.name}` : null,
    item.draftDeadline ? `Frist for utkast: ${formatDate(item.draftDeadline)}` : null,
    item.publishAt ? `Planlagt publisering: ${formatDate(item.publishAt)}` : null,
    bar.publishedAt
      ? `Publisert: ${formatDate(bar.publishedAt)}${bar.wasLate ? ' — etter planen' : ''}`
      : null,
  ];
  return lines.filter(Boolean).join('\n');
}
